import { FORM_ERROR } from 'final-form';
import { noop } from 'lodash';
import { FC, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { validatorEmail, validatorRequired } from '~src/services/validators';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import {
  PasswordBoxContainer,
  PasswordForm,
  PasswordHeader,
  PasswordSubline,
  PasswordTitleHeader,
  UnauthorizedPagesContainer,
} from '~src/outer_modules/shared/shared.sc';
import SvgIcon from '~src/shared/SvgIcon';
import IconInput, { InputIconType } from '~src/shared/form/inputs/IconInput';
import { getNewPassword } from '~src/store/session/actions';
import { FieldType } from '~src/types';
import { routes } from '~src/routingConfig';

import { BackLink, EmailSentBox, FormLink, SubmitBtn } from './restorePassword.sc';

interface FormValuesI {
  email: string;
}

const RestorePassword: FC = () => {
  const renderField = useCallback(
    ({ autoFocus, input, label, meta: { touched, error }, labelKey }: FieldType) => (
      <IconInput
        {...input}
        autoFocus={autoFocus}
        label={label}
        type={input.type as InputIconType}
        error={touched ? error : null}
        labelKey={labelKey}
      />
    ),
    [],
  );

  const onSubmit = (formValues: FormValuesI): Promise<any> => {
    return getNewPassword(formValues)
      .then(noop)
      .catch(() => ({ [FORM_ERROR]: 'unknown error' }));
  };

  const { t } = useTranslation();
  const emailPlaceholder = t('x.login.input.placeholder.email');

  return (
    <UnauthorizedPagesContainer>
      <PasswordBoxContainer>
        <Form onSubmit={onSubmit}>
          {({ handleSubmit, submitting, submitSucceeded }) =>
            submitSucceeded ? (
              <EmailSentBox>
                <SvgIcon width={60} name="iconLetter" />
                <PasswordHeader>
                  <PasswordTitleHeader data-translation="x.forgot_password.header.email_sent">
                    {t('x.forgot_password.header.email_sent')}
                  </PasswordTitleHeader>
                  <PasswordSubline data-translation="x.forgot_password.send.message">
                    {t('x.forgot_password.send.message')}
                  </PasswordSubline>
                </PasswordHeader>
                <BackLink to={routes.login} data-translation="x.forgot_password.send.link.back.text">
                  {t('x.forgot_password.send.link.back.text')}
                </BackLink>
              </EmailSentBox>
            ) : (
              <>
                <PasswordHeader>
                  <PasswordTitleHeader data-translation="x.forgot_password.header.text">
                    {t('x.forgot_password.header.text')}
                  </PasswordTitleHeader>
                  <PasswordSubline data-translation="x.forgot_password.header.subline.text">
                    {t('x.forgot_password.header.subline.text')}
                  </PasswordSubline>
                </PasswordHeader>

                <PasswordForm onSubmit={handleSubmit}>
                  <Field
                    autoFocus
                    name="email"
                    label={emailPlaceholder}
                    type="email"
                    children={renderField}
                    validate={composeValidators([validatorRequired, validatorEmail])}
                    labelKey="x.login.input.placeholder.email"
                  />
                  <FormLink to={routes.login} data-translation="x.forgot_password.initial.link.back.text">
                    {t('x.forgot_password.initial.link.back.text')}
                  </FormLink>
                  <SubmitBtn isLoading={submitting} $buttonColor="reddish">
                    <span data-translation="x.forgot_password.button.reset_password">
                      {t('x.forgot_password.button.reset_password')}
                    </span>
                  </SubmitBtn>
                </PasswordForm>
              </>
            )
          }
        </Form>
      </PasswordBoxContainer>
    </UnauthorizedPagesContainer>
  );
};

export default RestorePassword;
